import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Eye, FileText, Search, Trash2, Upload, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { documentService } from '../services/documentService';
import { vehicleService } from '../services/vehicleService';
import { trailerService } from '../services/trailerService';
import { staticDefinitionService } from '../services/staticDefinitionService';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { DriverBottomNav } from '../components/DriverBottomNav';

type DocumentTab = 'tractor' | 'trailer';
type DocumentTypeOption = { value: string; scope: DocumentTab[] };

type VehicleLikeItem = {
  id: string;
  plate: string;
  subtitle: string;
  driverName?: string;
};

const DEFAULT_DOCUMENT_TYPES = [
  'Vergi Levhasi',
  'Ehliyet',
  'Cekici Ruhsat',
  'Dorse Ruhsat',
  'SRC Belgesi',
  'Arac Sigortasi',
  'Tasima Sozlesmesi',
  'Tasiyici Yetki Belgesi',
  'Psikoteknik Belgesi',
];

const resolveFileUrl = (rawPath: string) => {
  if (!rawPath) return '';
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
};

const normalizePlate = (value?: string | null) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');

export function VehicleDocuments() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDriverMobile = location.pathname.startsWith('/driver-mobile-vehicle-documents');
  const storedDriverUser = isDriverMobile ? getStoredDriverMobileUser<any>() : null;

  const [vehicles, setVehicles] = useState<VehicleLikeItem[]>([]);
  const [trailers, setTrailers] = useState<VehicleLikeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<DocumentTab>((searchParams.get('tab') === 'trailer' ? 'trailer' : 'tractor'));
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleLikeItem | null>(null);
  const [selectedTrailer, setSelectedTrailer] = useState<VehicleLikeItem | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [matrixDocumentsLoading, setMatrixDocumentsLoading] = useState(false);
  const [allDocumentsByEntityId, setAllDocumentsByEntityId] = useState<Record<string, any[]>>({});
  const [uploading, setUploading] = useState(false);
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeOption[]>([]);
  const [form, setForm] = useState({ documentType: '', documentDate: '', notes: '' });
  const quickUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [quickUploadTargetItemId, setQuickUploadTargetItemId] = useState<string | null>(null);
  const [downloadingType, setDownloadingType] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    if (isDriverMobile && !storedDriverUser) {
      navigate('/driver-mobile-auth', { replace: true });
    }
  }, [isDriverMobile, navigate, storedDriverUser]);

  const activeEntityType = selectedTab === 'tractor' ? 'vehicle_document' : 'trailer_document';
  const selectedItem = selectedTab === 'tractor' ? selectedVehicle : selectedTrailer;

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        const [vehicleRes, trailerRes, definitionsRes] = await Promise.all([
          vehicleService.getVehicles({ ownerType: 'own' }).catch(() => ({ data: [] })),
          trailerService.getTrailers({ ownerType: 'own' }).catch(() => ({ data: [] })),
          staticDefinitionService.getAll().catch(() => ({ data: [] })),
        ]);

        const allVehicles: VehicleLikeItem[] = (Array.isArray((vehicleRes as any)?.data) ? (vehicleRes as any).data : []).map((item: any) => ({
          id: String(item.id || ''),
          plate: item.cekici_plaka || item.plate || '-',
          subtitle: [item.brand, item.model].filter(Boolean).join(' ').trim() || 'Çekici',
          driverName: item.driver_name || item.assigned_driver_name || item.full_name || item.user_full_name || '-',
        }));

        const allTrailers: VehicleLikeItem[] = (Array.isArray((trailerRes as any)?.data) ? (trailerRes as any).data : []).map((item: any) => ({
          id: String(item.id || ''),
          plate: item.plate || '-',
          subtitle: item.trailer_type || item.trailerType || 'Dorse',
          driverName: item.driver_name || item.assigned_driver_name || item.full_name || item.user_full_name || '-',
        }));

        const filteredVehicles = isDriverMobile
          ? allVehicles.filter((item) => {
              const assignedVehicleId = String(storedDriverUser?.assignedVehicleId || storedDriverUser?.assigned_vehicle_id || '');
              return (assignedVehicleId && item.id === assignedVehicleId) || normalizePlate(item.plate) === normalizePlate(storedDriverUser?.tractorPlate || storedDriverUser?.cekici_plaka);
            })
          : allVehicles;

        const filteredTrailers = isDriverMobile
          ? allTrailers.filter((item) => normalizePlate(item.plate) === normalizePlate(storedDriverUser?.trailerPlate || storedDriverUser?.dorse_plaka))
          : allTrailers;

        setVehicles(filteredVehicles);
        setTrailers(filteredTrailers);

        const allDefinitions = Array.isArray((definitionsRes as any)?.data) ? (definitionsRes as any).data : [];
        const selectedDefinition = allDefinitions.find((item: any) => String(item.key || '').trim() === 'tractor_trailer_document_types')
          || allDefinitions.find((item: any) => String(item.key || '').trim() === 'vehicle_document_types')
          || null;
        const nextTypes: DocumentTypeOption[] = Array.isArray(selectedDefinition?.items)
          ? selectedDefinition.items.map((item: any) => {
              const scope = Array.isArray(item.vehicleDocumentScope) ? item.vehicleDocumentScope.filter((value: string) => value === 'tractor' || value === 'trailer') : [];
              return { value: String(item.value || item.item_value || '').trim(), scope };
            }).filter((item: DocumentTypeOption) => item.value)
          : [];
        const resolvedTypes = nextTypes.length > 0 ? nextTypes : DEFAULT_DOCUMENT_TYPES.map((value) => ({ value, scope: [] }));
        setDocumentTypes(resolvedTypes);
        setForm((current) => ({ ...current, documentType: current.documentType || resolvedTypes[0]?.value || '' }));

        const initialPlate = normalizePlate(searchParams.get('plate'));
        const initialVehicleId = String(searchParams.get('vehicleId') || '');
        const initialTrailerId = String(searchParams.get('trailerId') || '');

        setSelectedVehicle(filteredVehicles.find((item) => item.id === initialVehicleId || normalizePlate(item.plate) === initialPlate) || filteredVehicles[0] || null);
        setSelectedTrailer(filteredTrailers.find((item) => item.id === initialTrailerId || normalizePlate(item.plate) === initialPlate) || filteredTrailers[0] || null);

      } catch (error) {
        console.error('Vehicle documents load failed:', error);
        toast.error('Çekici/Dorse belgeleri sayfası yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    void loadInitial();
  }, [isDriverMobile, storedDriverUser?.assignedVehicleId, storedDriverUser?.assigned_vehicle_id, storedDriverUser?.tractorPlate, storedDriverUser?.cekici_plaka, storedDriverUser?.trailerPlate, storedDriverUser?.dorse_plaka]);

  useEffect(() => {
    const nextTab = searchParams.get('tab') === 'trailer' ? 'trailer' : 'tractor';
    setSelectedTab(nextTab);
  }, [searchParams]);

  useEffect(() => {
    const plateParam = normalizePlate(searchParams.get('plate'));
    const vehicleIdParam = String(searchParams.get('vehicleId') || '');
    const trailerIdParam = String(searchParams.get('trailerId') || '');

    if (vehicles.length > 0) {
      setSelectedVehicle((current) => {
        const targeted = vehicles.find((item) => item.id === vehicleIdParam || normalizePlate(item.plate) === plateParam) || null;
        if (targeted) return targeted;
        if (current && vehicles.some((item) => String(item.id) === String(current.id))) return current;
        return vehicles[0] || null;
      });
    } else {
      setSelectedVehicle(null);
    }

    if (trailers.length > 0) {
      setSelectedTrailer((current) => {
        const targeted = trailers.find((item) => item.id === trailerIdParam || normalizePlate(item.plate) === plateParam) || null;
        if (targeted) return targeted;
        if (current && trailers.some((item) => String(item.id) === String(current.id))) return current;
        return trailers[0] || null;
      });
    } else {
      setSelectedTrailer(null);
    }
  }, [vehicles, trailers, searchParams]);

  useEffect(() => {
    if (!selectedItem?.id) {
      setDocuments([]);
      return;
    }

    const loadDocuments = async () => {
      try {
        setDocumentsLoading(true);
        const response: any = await documentService.getDocuments({ entityType: activeEntityType, entityId: selectedItem.id });
        setDocuments(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error('Vehicle documents load failed:', error);
        toast.error('Belgeler alınamadı');
      } finally {
        setDocumentsLoading(false);
      }
    };

    void loadDocuments();
  }, [activeEntityType, selectedItem?.id]);

  useEffect(() => {
    if (isDriverMobile) return;
    const source = selectedTab === 'tractor' ? vehicles : trailers;
    if (source.length === 0) {
      setAllDocumentsByEntityId({});
      return;
    }

    const loadMatrixDocuments = async () => {
      try {
        setMatrixDocumentsLoading(true);
        const entityIds = source.map((item) => String(item.id || '').trim()).filter(Boolean);
        if (entityIds.length === 0) {
          setAllDocumentsByEntityId({});
          return;
        }
        const response: any = await documentService.getDocuments({ entityType: activeEntityType, entityIds: entityIds.join(',') });
        const rows = Array.isArray(response?.data) ? response.data : [];
        const nextMap: Record<string, any[]> = {};
        rows.forEach((doc: any) => {
          const entityId = String(doc.entity_id || doc.entityId || '');
          if (!entityId) return;
          if (!nextMap[entityId]) nextMap[entityId] = [];
          nextMap[entityId].push(doc);
        });
        Object.keys(nextMap).forEach((key) => {
          nextMap[key] = nextMap[key].sort((left: any, right: any) => new Date(right.uploaded_at || 0).getTime() - new Date(left.uploaded_at || 0).getTime());
        });
        setAllDocumentsByEntityId(nextMap);
      } catch (error) {
        console.error('Vehicle documents matrix load failed:', error);
        setAllDocumentsByEntityId({});
      } finally {
        setMatrixDocumentsLoading(false);
      }
    };

    void loadMatrixDocuments();
  }, [activeEntityType, isDriverMobile, selectedTab, vehicles, trailers]);

  const filteredItems = useMemo(() => {
    const source = selectedTab === 'tractor' ? vehicles : trailers;
    const query = searchTerm.trim().toLocaleLowerCase('tr-TR');
    if (!query) return source;
    return source.filter((item) => `${item.plate} ${item.subtitle}`.toLocaleLowerCase('tr-TR').includes(query));
  }, [selectedTab, vehicles, trailers, searchTerm]);

  const handleSelectItem = (tab: DocumentTab, item: VehicleLikeItem) => {
    if (tab === 'tractor') setSelectedVehicle(item);
    else setSelectedTrailer(item);
    setSelectedTab(tab);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', tab);
      next.set('plate', item.plate);
      if (tab === 'tractor') {
        next.set('vehicleId', item.id);
        next.delete('trailerId');
      } else {
        next.set('trailerId', item.id);
        next.delete('vehicleId');
      }
      return next;
    });
  };

  const handleTabChange = (tab: DocumentTab) => {
    setSelectedTab(tab);
    setSearchTerm('');
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', tab);

      if (tab === 'tractor') {
        const target = selectedVehicle || vehicles[0] || null;
        next.delete('trailerId');
        if (target) {
          next.set('vehicleId', target.id);
          next.set('plate', target.plate);
        } else {
          next.delete('vehicleId');
          next.delete('plate');
        }
      } else {
        const target = selectedTrailer || trailers[0] || null;
        next.delete('vehicleId');
        if (target) {
          next.set('trailerId', target.id);
          next.set('plate', target.plate);
        } else {
          next.delete('trailerId');
          next.delete('plate');
        }
      }
      return next;
    });
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const uploadTargetId = quickUploadTargetItemId || selectedItem?.id || null;
    if (!file || !uploadTargetId) return;
    if (!form.documentType) {
      toast.error('Evrak türü seçin');
      event.target.value = '';
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece PDF, JPG, PNG veya WEBP yüklenebilir');
      event.target.value = '';
      return;
    }
    try {
      setUploading(true);
      const payload = new FormData();
      payload.append('file', file);
      payload.append('entityType', activeEntityType);
      payload.append('entityId', uploadTargetId);
      payload.append('documentType', form.documentType);
      payload.append('name', file.name);
      if (form.documentDate) payload.append('documentDate', form.documentDate);
      if (form.notes) payload.append('notes', form.notes);
      await documentService.uploadDocument(payload);
      const refreshed: any = await documentService.getDocuments({ entityType: activeEntityType, entityId: uploadTargetId });
      setDocuments(Array.isArray(refreshed?.data) ? refreshed.data : []);
      setAllDocumentsByEntityId((current) => ({ ...current, [uploadTargetId]: Array.isArray(refreshed?.data) ? refreshed.data : [] }));
      setForm((current) => ({ ...current, documentDate: '', notes: '' }));
      toast.success('Belge yüklendi');
    } catch (error) {
      console.error('Vehicle document upload failed:', error);
      toast.error('Belge yüklenemedi');
    } finally {
      setUploading(false);
      setQuickUploadTargetItemId(null);
      event.target.value = '';
    }
  };

  const handleQuickUploadClick = (documentType: string, itemId?: string) => {
    const resolvedItemId = itemId || selectedItem?.id || null;
    if (!resolvedItemId || isDriverMobile) return;
    setForm((current) => ({ ...current, documentType }));
    setQuickUploadTargetItemId(String(resolvedItemId));
    quickUploadInputRef.current?.click();
  };

  const handleDownloadTypeZip = async (documentType: string) => {
    if (downloadingType) return;
    setDownloadingType(documentType);
    try {
      const { blob, fileName } = await documentService.downloadZip({ entityType: activeEntityType, documentType });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success('Belgeler zip olarak indirildi');
    } catch (error) {
      console.error('Vehicle document zip download failed:', error);
      toast.error(error instanceof Error ? error.message : 'Zip indirilemedi');
    } finally {
      setDownloadingType(null);
    }
  };

  const handleDelete = async (documentId: string | number) => {
    try {
      await documentService.deleteDocument(documentId);
      setDocuments((current) => current.filter((item) => String(item.id) !== String(documentId)));
      toast.success('Belge silindi');
    } catch (error) {
      console.error('Vehicle document delete failed:', error);
      toast.error('Belge silinemedi');
    }
  };

  const wrapperClass = isDriverMobile ? 'safe-area-page min-h-dvh bg-gray-50' : 'space-y-6';
  const latestDocuments = useMemo(() => {
    const grouped = new Map<string, any[]>();
    documents.forEach((doc) => {
      const key = String(doc.document_type || '').trim() || 'other';
      const current = grouped.get(key) || [];
      current.push(doc);
      grouped.set(key, current);
    });
    return Array.from(grouped.entries()).map(([documentType, items]) => {
      const sorted = [...items].sort((left, right) => new Date(right.uploaded_at || 0).getTime() - new Date(left.uploaded_at || 0).getTime());
      return {
        documentType,
        count: sorted.length,
        latest: sorted[0],
        items: sorted,
      };
    });
  }, [documents]);

  const matrixItems = useMemo(() => (selectedTab === 'tractor' ? filteredItems : filteredItems), [filteredItems, selectedTab]);
  const activeDocumentTypes = useMemo(() => documentTypes
    .filter((item) => item.scope.length === 0 || item.scope.includes(selectedTab))
    .map((item) => item.value), [documentTypes, selectedTab]);

  useEffect(() => {
    if (activeDocumentTypes.length === 0) return;
    setForm((current) => activeDocumentTypes.includes(current.documentType) ? current : { ...current, documentType: activeDocumentTypes[0] || '' });
  }, [activeDocumentTypes]);

  const matrixRows = useMemo(() => matrixItems.map((item) => {
    const docs = allDocumentsByEntityId[String(item.id)] || [];
    const byType = new Map<string, any[]>();
    docs.forEach((doc) => {
      const key = String(doc.document_type || '').trim() || 'other';
      const current = byType.get(key) || [];
      current.push(doc);
      byType.set(key, current);
    });
    return { item, byType };
  }), [allDocumentsByEntityId, matrixItems]);

  const matrixSummary = useMemo(() => {
    const totalSlots = matrixRows.length * activeDocumentTypes.length;
    let missingCount = 0;
    matrixRows.forEach(({ byType }) => {
      activeDocumentTypes.forEach((type) => {
        if ((byType.get(type) || []).length === 0) missingCount += 1;
      });
    });
    return {
      rowCount: matrixRows.length,
      totalSlots,
      missingCount,
      completedCount: Math.max(0, totalSlots - missingCount),
    };
  }, [activeDocumentTypes, matrixRows]);

  const visibleMatrixRows = useMemo(() => {
    if (!showOnlyMissing) return matrixRows;
    return matrixRows.filter(({ byType }) => activeDocumentTypes.some((type) => (byType.get(type) || []).length === 0));
  }, [activeDocumentTypes, matrixRows, showOnlyMissing]);

  const totalPages = Math.max(1, Math.ceil(visibleMatrixRows.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const paginatedRows = visibleMatrixRows.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [showOnlyMissing, searchTerm, selectedTab, pageSize]);

  const openMatrixDocument = (doc: any) => {
    const url = resolveFileUrl(doc.file_path || doc.file_url || '');
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={wrapperClass}>
      {isDriverMobile ? (
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/driver-mobile-profile')} className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 font-semibold text-slate-900 hover:bg-slate-200">
              <ArrowLeft className="h-5 w-5" /> Geri
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Çekici / Dorse Belgeleri</h1>
              <p className="text-sm text-slate-500">Araç evraklarınızı görüntüleyin</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Çekici / Dorse Belgeleri</h1>
            <p className="mt-1 text-gray-500">Çekici ve dorseler için evrakları yükleyin ve yönetin</p>
          </div>
        </div>
      )}

      <div className={`grid gap-6 ${isDriverMobile ? 'p-4' : 'lg:grid-cols-[320px_minmax(0,1fr)]'}`}>
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => handleTabChange('tractor')} className={`rounded-xl px-4 py-2 text-sm font-semibold ${selectedTab === 'tractor' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Çekici</button>
            <button type="button" onClick={() => handleTabChange('trailer')} className={`rounded-xl px-4 py-2 text-sm font-semibold ${selectedTab === 'trailer' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Dorse</button>
          </div>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={selectedTab === 'tractor' ? 'Çekici ara' : 'Dorse ara'} className="w-full rounded-xl border border-gray-300 py-2 pl-9 pr-3" />
          </div>
          <div key={selectedTab} className="max-h-[70vh] space-y-2 overflow-y-auto">
            {loading ? <div className="py-8 text-center text-sm text-gray-500">Yükleniyor...</div> : filteredItems.map((item) => {
              const isSelected = selectedTab === 'tractor' ? String(selectedVehicle?.id) === String(item.id) : String(selectedTrailer?.id) === String(item.id);
              return (
                <button key={`${selectedTab}-${item.id}`} type="button" onClick={() => handleSelectItem(selectedTab, item)} className={`w-full rounded-xl border px-4 py-3 text-left transition ${isSelected ? 'border-violet-500 bg-violet-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                  <div className="font-semibold text-gray-900">{item.plate}</div>
                  <div className="mt-1 text-xs text-gray-500">{item.subtitle || '-'}</div>
                  <div className="mt-1 text-xs text-gray-400">Surucu: {item.driverName || '-'}</div>
                </button>
              );
            })}
            {!loading && filteredItems.length === 0 ? <div className="py-8 text-center text-sm text-gray-500">Kayıt bulunamadı</div> : null}
          </div>
        </div>

        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          {!isDriverMobile ? (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Plaka Sayısı</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{matrixSummary.rowCount}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Toplam Belge Slotu</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{matrixSummary.totalSlots}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Yüklü</div>
                  <div className="mt-1 text-2xl font-bold text-emerald-700">{matrixSummary.completedCount}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Eksik</div>
                  <div className="mt-1 text-2xl font-bold text-rose-700">{matrixSummary.missingCount}</div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                <div className="text-sm text-slate-600">Belge matrisi görünümü</div>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={showOnlyMissing} onChange={(e) => setShowOnlyMissing(e.target.checked)} className="rounded border-slate-300" />
                  Sadece eksikleri göster
                </label>
              </div>

            <div className="overflow-hidden rounded-xl border border-black">
              <div className="max-h-[70vh] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="sticky left-0 top-0 z-40 min-w-[220px] border-b border-r border-black bg-gray-50 px-4 py-3 text-left font-medium text-gray-600">{selectedTab === 'tractor' ? 'Çekici' : 'Dorse'}</th>
                      {activeDocumentTypes.map((type) => (
                        <th key={type} className="sticky top-0 z-20 min-w-[200px] border-b border-r border-black bg-gray-50 px-4 py-3 text-left font-medium text-gray-600">
                          <div className="space-y-2">
                            <div>{type}</div>
                            {!isDriverMobile && selectedItem ? (
                              <div className="flex flex-wrap gap-1.5">
                                <button type="button" onClick={() => handleQuickUploadClick(type)} className="inline-flex items-center gap-1 rounded-lg bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-200">
                                  <Upload className="h-3.5 w-3.5" /> Yükle
                                </button>
                                <button type="button" onClick={() => void handleDownloadTypeZip(type)} disabled={downloadingType === type} className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-60">
                                  <Download className="h-3.5 w-3.5" /> {downloadingType === type ? 'Zipleniyor...' : 'Zip İndir'}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixDocumentsLoading ? (
                      <tr><td colSpan={activeDocumentTypes.length + 1} className="px-4 py-8 text-center text-gray-500">Yükleniyor...</td></tr>
                    ) : visibleMatrixRows.length === 0 ? (
                      <tr><td colSpan={activeDocumentTypes.length + 1} className="px-4 py-8 text-center text-gray-500">Kayıt bulunamadı</td></tr>
                    ) : paginatedRows.map(({ item, byType }) => (
                      <tr key={`matrix-${selectedTab}-${item.id}`} className="border-t border-black">
                        <td className="sticky left-0 z-[1] border-r border-black bg-white px-4 py-3 align-top">
                          <button type="button" onClick={() => handleSelectItem(selectedTab, item)} className="text-left">
                            <div className="font-semibold text-gray-900 hover:text-violet-700">{item.plate}</div>
                            <div className="mt-1 text-xs text-gray-500">{item.subtitle || '-'}</div>
                            <div className="mt-1 text-xs text-gray-400">Sürücü: {item.driverName || '-'}</div>
                          </button>
                        </td>
                        {activeDocumentTypes.map((type) => {
                          const docs = byType.get(type) || [];
                          const latest = docs[0] || null;
                          const previewUrl = latest ? resolveFileUrl(latest.file_path || latest.file_url || '') : '';
                          const isImage = String(latest?.mime_type || '').startsWith('image/');
                          return (
                            <td key={`${item.id}-${type}`} className={`border-r border-black px-4 py-3 align-top ${latest ? 'bg-white' : 'bg-rose-50/70'}`}>
                              {latest ? (
                                <div className="space-y-2">
                                  {isImage && previewUrl ? (
                                    <button type="button" onClick={() => openMatrixDocument(latest)} className="block overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                                      <img src={previewUrl} alt={latest.file_name || 'Belge'} className="h-20 w-full object-cover" />
                                    </button>
                                  ) : null}
                                  <div className="text-xs font-medium text-gray-700">{docs.length} belge</div>
                                  <div className="truncate text-xs text-gray-500">{latest.file_name || 'Belge'}</div>
                                  <div className="flex flex-wrap gap-2">
                                    <button type="button" onClick={() => openMatrixDocument(latest)} className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100">
                                      <Eye className="h-3.5 w-3.5" /> Önizle
                                    </button>
                                    {!isDriverMobile ? (
                                      <button type="button" onClick={() => void handleDelete(latest.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
                                        <Trash2 className="h-3.5 w-3.5" /> Sil
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="text-xs font-semibold text-rose-700">Eksik</div>
                                  <div className="text-xs text-rose-500">Bu belge yüklenmemiş.</div>
                                  {!isDriverMobile ? (
                                    <button type="button" onClick={() => handleQuickUploadClick(type, item.id)} className="inline-flex items-center gap-1 rounded-lg bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-200">
                                      <Upload className="h-3.5 w-3.5" /> Yükle
                                    </button>
                                  ) : null}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-3 border-t border-black bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-slate-600">
                  {visibleMatrixRows.length === 0
                    ? 'Kayıt yok'
                    : `${(clampedPage - 1) * pageSize + 1}-${Math.min(clampedPage * pageSize, visibleMatrixRows.length)} / ${visibleMatrixRows.length} kayıt`}
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>{size} / sayfa</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => setPage(clampedPage - 1)} disabled={clampedPage <= 1} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
                    <ChevronLeft className="h-4 w-4" /> Önceki
                  </button>
                  <span className="text-sm font-medium text-slate-700">Sayfa {clampedPage} / {totalPages}</span>
                  <button type="button" onClick={() => setPage(clampedPage + 1)} disabled={clampedPage >= totalPages} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
                    Sonraki <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            </div>
          ) : null}

          {selectedItem ? (
            <>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-violet-100 p-3 text-violet-700"><Truck className="h-5 w-5" /></div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{selectedItem.plate}</div>
                    <div className="text-sm text-gray-500">{selectedItem.subtitle}</div>
                    <div className="text-xs text-gray-500">Surucu: {selectedItem.driverName || '-'}</div>
                  </div>
                </div>

              {latestDocuments.length > 0 ? (
                <div>
                  <div className="mb-2 text-sm font-medium text-gray-700">Evrak Onizlemeleri</div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {latestDocuments.map((group) => {
                      const doc = group.latest;
                      const url = resolveFileUrl(doc.file_path || doc.file_url || '');
                      const isImage = String(doc.mime_type || '').startsWith('image/');
                      return (
                        <button key={`${group.documentType}-${doc.id}`} type="button" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')} className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 text-left hover:border-violet-300 hover:bg-violet-50">
                          <div className="flex h-32 items-center justify-center bg-white">
                            {isImage ? <img src={url} alt={doc.file_name || 'Belge'} className="h-full w-full object-cover" /> : <div className="flex flex-col items-center gap-2 text-gray-500"><FileText className="h-8 w-8" /><span className="text-xs font-medium">PDF</span></div>}
                          </div>
                          <div className="space-y-1 p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-xs font-semibold text-violet-700">{group.documentType || '-'}</div>
                              <div className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">{group.count} dosya</div>
                            </div>
                            <div className="truncate text-sm text-gray-800">{doc.file_name || 'Belge'}</div>
                            <div className="text-[11px] text-gray-500">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('tr-TR') : '-'}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {!isDriverMobile ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <input ref={quickUploadInputRef} type="file" accept="application/pdf,.pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} />
                  <div className="grid gap-4 md:grid-cols-[240px_180px_minmax(0,1fr)_220px] md:items-end">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Evrak Türü</label>
                      <select value={form.documentType} onChange={(e) => setForm((current) => ({ ...current, documentType: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                        <option value="">Seçiniz</option>
                        {activeDocumentTypes.map((item) => <option key={item} value={item}>{item}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Evrak Tarihi</label>
                      <input type="date" value={form.documentDate} onChange={(e) => setForm((current) => ({ ...current, documentDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Not</label>
                      <input type="text" value={form.notes} onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="İsteğe bağlı not" />
                    </div>
                    <div>
                      <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-white hover:bg-violet-700">
                        <Upload className="h-4 w-4" />
                        {uploading ? 'Yükleniyor...' : 'PDF / JPG Yükle'}
                        <input type="file" accept="application/pdf,.pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} />
                      </label>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">Evrak türleri Ayarlar &gt; Statik Liste Tanımları altında once <code>tractor_trailer_document_types</code>, yoksa <code>vehicle_document_types</code> anahtarindan yonetilir.</p>
                </div>
              ) : null}

              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Tür</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Evrak Tarihi</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Dosya</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Not</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Yüklenme</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentsLoading ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Yükleniyor...</td></tr>
                    ) : documents.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Henüz belge yüklenmemiş</td></tr>
                    ) : documents.map((doc) => (
                      <tr key={doc.id} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-gray-900">{doc.document_type || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{doc.document_date ? new Date(doc.document_date).toLocaleDateString('tr-TR') : '-'}</td>
                        <td className="px-4 py-3 text-gray-900">{doc.file_name || 'Belge'}</td>
                        <td className="px-4 py-3 text-gray-600">{doc.notes || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('tr-TR') : '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => window.open(resolveFileUrl(doc.file_path || doc.file_url || ''), '_blank', 'noopener,noreferrer')} className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1.5 text-blue-700 hover:bg-blue-100">
                              <FileText className="h-4 w-4" /> Görüntüle
                            </button>
                            {!isDriverMobile ? (
                              <button type="button" onClick={() => void handleDelete(doc.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-red-700 hover:bg-red-100">
                                <Trash2 className="h-4 w-4" /> Sil
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="py-16 text-center text-gray-500">Belgeleri görmek için soldan bir kayıt seçin</div>
          )}
        </div>
      </div>

      {isDriverMobile ? <DriverBottomNav /> : null}
    </div>
  );
}
